import { apiRequest } from './axiosConfig';

// Platform types
export type AdminPlatformType = 'instagram' | 'facebook' | 'youtube' | 'x' | 'tiktok' | 'line' | 'yahoo_banner' | 'google_banner' | 'smartnews' | 'website' | 'mailmag';

// 小カテゴリ（x-ad中分類）型定義。slugは "big/mid" 形式（例: finance/investment）
export interface ImageStyleSubcategory {
  id: number;
  image_style_category_id: number;
  slug: string;
  name: string;
  sort_order: number;
  is_active: boolean;
}

// ImageStyleCategory型定義
export interface ImageStyleCategory {
  id: number;
  name: string;
  slug: string;
  description?: string;
  sort_order: number;
  is_active: boolean;
  styles_count?: number;
  subcategories?: ImageStyleSubcategory[];
  created_at?: string;
  updated_at?: string;
}

export interface ImageStyleCategoryCreateRequest {
  name: string;
  slug?: string;
  description?: string;
  sort_order?: number;
  is_active?: boolean;
}

export interface ImageStyleCategoryUpdateRequest {
  name: string;
  slug?: string;
  description?: string;
  sort_order?: number;
  is_active?: boolean;
}

// ImageStyle型定義
export interface AdminImageStyle {
  id: number;
  category: string;
  category_id?: number;
  platforms?: AdminPlatformType[];
  aspect_ratio?: string;
  name: string;
  /** 一覧 API（GET admin/image-styles）は返さない。全文は getStyle(id) で取る */
  prompt_template?: string;
  description?: string;
  tags?: string[];
  reference_image_url?: string;
  thumbnail_url?: string;
  sort_order: number;
  is_active: boolean;
  style_category?: ImageStyleCategory;
  subcategory_id?: number | null;
  subcategory?: ImageStyleSubcategory | null;
  created_at: string;
  updated_at: string;
}

export interface ImageStyleCreateRequest {
  category?: string;
  category_id?: number;
  subcategory_id?: number | null;
  platforms?: AdminPlatformType[];
  aspect_ratio?: string;
  name: string;
  prompt_template: string;
  description?: string;
  tags?: string[];
  reference_image_url?: string;
  thumbnail_url?: string;
  sort_order?: number;
  is_active?: boolean;
}

export interface ImageStyleUpdateRequest {
  category?: string;
  category_id?: number;
  subcategory_id?: number | null;
  platforms?: AdminPlatformType[];
  aspect_ratio?: string;
  name: string;
  prompt_template: string;
  description?: string;
  tags?: string[];
  reference_image_url?: string;
  thumbnail_url?: string;
  sort_order?: number;
  is_active?: boolean;
}

interface ImageStylesResponse {
  success: boolean;
  data: AdminImageStyle[];
}

interface ImageStyleResponse {
  success: boolean;
  data: AdminImageStyle;
  message?: string;
}

interface ImageStyleDeleteResponse {
  success: boolean;
  message: string;
}

interface CategoriesResponse {
  success: boolean;
  data: ImageStyleCategory[];
}

interface CategoryResponse {
  success: boolean;
  data: ImageStyleCategory;
  message?: string;
}

interface CategoryDeleteResponse {
  success: boolean;
  message: string;
}

interface ImageUploadResponse {
  success: boolean;
  message: string;
  data: {
    path: string;
    url: string;
    disk: string;
  };
}

interface GetImageUrlResponse {
  success: boolean;
  data: {
    url: string;
  };
}

/**
 * 管理者用ImageStyle APIサービス
 */
const adminImageStyleService = {
  /**
   * 全画像スタイル一覧取得（管理者用）
   */
  async getAllStyles(): Promise<AdminImageStyle[]> {
    const response = await apiRequest.get<ImageStylesResponse>('admin/image-styles');
    return response.data;
  },

  /**
   * 画像スタイル詳細取得
   */
  async getStyle(id: number): Promise<AdminImageStyle> {
    const response = await apiRequest.get<ImageStyleResponse>(`admin/image-styles/${id}`);
    return response.data;
  },

  /**
   * 画像スタイル作成
   */
  async createStyle(data: ImageStyleCreateRequest): Promise<AdminImageStyle> {
    const response = await apiRequest.post<ImageStyleResponse>('admin/image-styles', data);
    return response.data;
  },

  /**
   * 画像スタイル更新
   */
  async updateStyle(id: number, data: ImageStyleUpdateRequest): Promise<AdminImageStyle> {
    const response = await apiRequest.put<ImageStyleResponse>(`admin/image-styles/${id}`, data);
    return response.data;
  },

  /**
   * 画像スタイル削除
   */
  async deleteStyle(id: number): Promise<void> {
    await apiRequest.delete<ImageStyleDeleteResponse>(`admin/image-styles/${id}`);
  },

  /**
   * 画像スタイルの有効/無効切り替え
   */
  async toggleStyle(id: number): Promise<AdminImageStyle> {
    const response = await apiRequest.put<ImageStyleResponse>(`admin/image-styles/${id}/toggle`);
    return response.data;
  },

  /**
   * 画像スタイルの並び順更新
   */
  async updateOrder(styles: { id: number; sort_order: number }[]): Promise<void> {
    await apiRequest.put('admin/image-styles/order', { styles });
  },

  /**
   * カテゴリ一覧取得（管理用 - 全カテゴリ + スタイル数）
   */
  async getCategories(): Promise<ImageStyleCategory[]> {
    const response = await apiRequest.get<CategoriesResponse>('admin/image-style-categories');
    return response.data;
  },

  /**
   * アクティブなカテゴリのみ取得（ドロップダウン用）
   */
  async getActiveCategories(): Promise<ImageStyleCategory[]> {
    const response = await apiRequest.get<CategoriesResponse>('admin/image-style-categories/active');
    return response.data;
  },

  /**
   * カテゴリ詳細取得
   */
  async getCategory(id: number): Promise<ImageStyleCategory> {
    const response = await apiRequest.get<CategoryResponse>(`admin/image-style-categories/${id}`);
    return response.data;
  },

  /**
   * カテゴリ作成
   */
  async createCategory(data: ImageStyleCategoryCreateRequest): Promise<ImageStyleCategory> {
    const response = await apiRequest.post<CategoryResponse>('admin/image-style-categories', data);
    return response.data;
  },

  /**
   * カテゴリ更新
   */
  async updateCategory(id: number, data: ImageStyleCategoryUpdateRequest): Promise<ImageStyleCategory> {
    const response = await apiRequest.put<CategoryResponse>(`admin/image-style-categories/${id}`, data);
    return response.data;
  },

  /**
   * カテゴリ削除
   */
  async deleteCategory(id: number): Promise<void> {
    await apiRequest.delete<CategoryDeleteResponse>(`admin/image-style-categories/${id}`);
  },

  /**
   * カテゴリの有効/無効切り替え
   */
  async toggleCategory(id: number): Promise<ImageStyleCategory> {
    const response = await apiRequest.put<CategoryResponse>(`admin/image-style-categories/${id}/toggle`);
    return response.data;
  },

  /**
   * カテゴリの並び順更新
   */
  async updateCategoryOrder(categories: { id: number; sort_order: number }[]): Promise<void> {
    await apiRequest.put('admin/image-style-categories/order', { categories });
  },

  /**
   * 画像アップロード
   * @param file アップロードする画像ファイル
   * @param type 画像タイプ（reference: 参照画像, thumbnail: サムネイル）
   */
  async uploadImage(file: File, type: 'reference' | 'thumbnail'): Promise<{ path: string; url: string }> {
    const formData = new FormData();
    formData.append('image', file);
    formData.append('type', type);

    const response = await apiRequest.post<ImageUploadResponse>(
      'admin/image-styles/upload-image',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return {
      path: response.data.path,
      url: response.data.url
    };
  },

  /**
   * 画像の署名付きURL取得
   * @param path S3のパス
   */
  async getImageUrl(path: string): Promise<string> {
    const response = await apiRequest.post<GetImageUrlResponse>('admin/image-styles/get-image-url', { path });
    return response.data.url;
  }
};

export default adminImageStyleService;
